import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-localization": "en",
  },
});

let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];
let refreshTimer = null;

const REFRESH_BUFFER_TIME = 5 * 60 * 1000;

// ============================================================
// PUBLIC APIs
// These APIs DO NOT require access token
// ============================================================

const PUBLIC_ENDPOINTS = [
  "login-panel/login",
  "login-panel/forgotPassword",
  "login-panel/resetPassword",
  "login-panel/refresh-access-token",
];

// ============================================================
// CHECK PUBLIC API
// ============================================================

const isPublicEndpoint = (url = "") => {
  return PUBLIC_ENDPOINTS.some((endpoint) =>
    url.includes(endpoint)
  );
};

// ============================================================
// NORMALIZE API MESSAGE
// ============================================================

const normalizeApiMessage = (payload) => {
  const displayMessage = payload?.displayMessage;

  if (!displayMessage) {
    return payload;
  }

  toast.error(displayMessage, {
    toastId: `api-error-${
      payload?.errorCode || displayMessage
    }`,
  });

  return {
    ...payload,
    message: displayMessage,
    errorMessage: displayMessage,
  };
};

// ============================================================
// NORMALIZE API ERROR
// ============================================================

const normalizeApiError = (error) =>
  normalizeApiMessage(
    error?.response?.data || error
  );

// ============================================================
// PROCESS FAILED QUEUE
// ============================================================

const processQueue = (
  error,
  token = null
) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

// ============================================================
// CLEAR REFRESH TIMER
// ============================================================

const clearRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

// ============================================================
// LOGOUT
// ============================================================

const logoutAndRedirect = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("tokenExpiry");

  clearRefreshTimer();

  window.location.href = "/";
};

// ============================================================
// SCHEDULE TOKEN REFRESH
// ============================================================

const scheduleTokenRefresh = (expiresIn) => {
  clearRefreshTimer();

  const refreshTime = Math.max(
    expiresIn - REFRESH_BUFFER_TIME,
    60000
  );

  console.log(
    `Token refresh scheduled in ${
      refreshTime / 1000
    } seconds`
  );

  refreshTimer = setTimeout(() => {
    console.log(
      "Proactive token refresh triggered"
    );

    refreshAccessToken().catch((err) => {
      console.error(
        "Proactive refresh failed:",
        err
      );
    });
  }, refreshTime);
};

// ============================================================
// REFRESH ACCESS TOKEN
// ============================================================

const refreshAccessToken = () => {
  if (isRefreshing) {
    return new Promise(
      (resolve, reject) => {
        failedQueue.push({
          resolve,
          reject,
        });
      }
    );
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    const refreshToken =
      localStorage.getItem("refreshToken");

    if (!refreshToken) {
      const err = new Error(
        "Refresh token missing"
      );

      processQueue(err);

      logoutAndRedirect();

      throw err;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/login-panel/refresh-access-token`,
        {
          refreshToken,
        },
        {
          headers: {
            "Content-Type":
              "application/json",
          },
        }
      );

      const data = response?.data;

      if (!data?.status) {
        throw new Error(
          data?.message ||
            "Refresh API returned a failure status"
        );
      }

      const newAccessToken =
        data?.data?.token;

      if (!newAccessToken) {
        throw new Error(
          "Access token not returned by refresh API"
        );
      }

      // --------------------------------------------------------
      // SAVE NEW ACCESS TOKEN
      // --------------------------------------------------------

      localStorage.setItem(
        "accessToken",
        newAccessToken
      );

      // --------------------------------------------------------
      // SAVE NEW REFRESH TOKEN
      // --------------------------------------------------------

      const newRefreshToken =
        data?.data?.refreshToken;

      if (newRefreshToken) {
        localStorage.setItem(
          "refreshToken",
          newRefreshToken
        );
      }

      // --------------------------------------------------------
      // TOKEN EXPIRY
      // --------------------------------------------------------

      const expiresIn =
        data?.data?.expiresIn ||
        24 * 60 * 60 * 1000;

      localStorage.setItem(
        "tokenExpiry",
        Date.now() + expiresIn
      );

      // --------------------------------------------------------
      // SCHEDULE NEXT REFRESH
      // --------------------------------------------------------

      scheduleTokenRefresh(expiresIn);

      // --------------------------------------------------------
      // PROCESS QUEUE
      // --------------------------------------------------------

      processQueue(
        null,
        newAccessToken
      );

      return newAccessToken;
    } catch (refreshError) {
      processQueue(refreshError);

      logoutAndRedirect();

      throw refreshError;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ============================================================
// REQUEST INTERCEPTOR
// ============================================================

api.interceptors.request.use(
  async (config) => {
    const requestUrl =
      config?.url || "";

    // --------------------------------------------------------
    // PUBLIC API
    // Do NOT check access token
    // Do NOT refresh token
    // --------------------------------------------------------

    if (
      isPublicEndpoint(requestUrl)
    ) {
      console.log(
        "Public API:",
        requestUrl
      );

      return config;
    }

    // --------------------------------------------------------
    // PROTECTED API
    // --------------------------------------------------------

    let accessToken =
      localStorage.getItem(
        "accessToken"
      );

    // --------------------------------------------------------
    // NO ACCESS TOKEN
    // Try refresh
    // --------------------------------------------------------

    if (!accessToken) {
      try {
        accessToken =
          await refreshAccessToken();
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // --------------------------------------------------------
    // ADD AUTHORIZATION HEADER
    // --------------------------------------------------------

    if (accessToken) {
      config.headers =
        config.headers || {};

      config.headers.Authorization =
        `Bearer ${accessToken}`;
    }

    return config;
  },

  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================
// RESPONSE INTERCEPTOR
// ============================================================

api.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error) => {
    const originalRequest =
      error?.config;

    // --------------------------------------------------------
    // NO RESPONSE
    // --------------------------------------------------------

    if (!error.response) {
      return Promise.reject(error);
    }

    // --------------------------------------------------------
    // DISPLAY MESSAGE
    // --------------------------------------------------------

    if (
      error?.response?.data
        ?.displayMessage
    ) {
      error.message =
        error.response.data.displayMessage;
    }

    // --------------------------------------------------------
    // PUBLIC APIs
    // Never refresh token for these APIs
    // --------------------------------------------------------

    if (
      isPublicEndpoint(
        originalRequest?.url || ""
      )
    ) {
      return Promise.reject(error);
    }

    // --------------------------------------------------------
    // 401 - ACCESS TOKEN EXPIRED
    // --------------------------------------------------------

    if (
      error.response.status === 401 &&
      !originalRequest?._retry
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken =
          await refreshAccessToken();

        originalRequest.headers =
          originalRequest.headers || {};

        originalRequest.headers.Authorization =
          `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(
          refreshError
        );
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================
// INITIALIZE TOKEN REFRESH
// ============================================================

export const initializeTokenRefresh = (
  expiresIn = 24 * 60 * 60 * 1000
) => {
  scheduleTokenRefresh(expiresIn);
};

// ============================================================
// GET
// ============================================================

export const GetData = (
  endpoint,
  params = {}
) =>
  api
    .get(endpoint, { params })
    .then((res) =>
      normalizeApiMessage(
        res.data
      )
    );

// ============================================================
// POST
// ============================================================

export const PostData = (
  endpoint,
  data = {}
) =>
  api
    .post(endpoint, data)
    .then((res) =>
      normalizeApiMessage(
        res.data
      )
    );

// ============================================================
// PUT
// ============================================================

export const PutData = (
  endpoint,
  data = {}
) =>
  api
    .put(endpoint, data)
    .then((res) =>
      normalizeApiMessage(
        res.data
      )
    );

// ============================================================
// DELETE
// ============================================================

export const DeleteData = (
  endpoint
) =>
  api
    .delete(endpoint)
    .then((res) =>
      normalizeApiMessage(
        res.data
      )
    );

// ============================================================
// GET WITH TOKEN
// ============================================================

export const GetDataWithToken = async (
  endpoint,
  params = {}
) => {
  try {
    const res = await api.get(
      endpoint,
      { params }
    );

    return normalizeApiMessage(
      res.data
    );
  } catch (error) {
    return normalizeApiError(
      error
    );
  }
};

// ============================================================
// POST WITH TOKEN
// ============================================================

export const PostDataWithToken = async (
  endpoint,
  data = {}
) => {
  try {
    const res = await api.post(
      endpoint,
      data
    );

    return normalizeApiMessage(
      res.data
    );
  } catch (error) {
    return normalizeApiError(
      error
    );
  }
};

// ============================================================
// PUT WITH TOKEN
// ============================================================

export const PutDataWithToken = async (
  endpoint,
  data = {}
) => {
  try {
    const res = await api.put(
      endpoint,
      data
    );

    return normalizeApiMessage(
      res.data
    );
  } catch (error) {
    return normalizeApiError(
      error
    );
  }
};

// ============================================================
// DELETE WITH TOKEN
// ============================================================

export const DeleteDataWithToken = async (
  endpoint
) => {
  try {
    const res = await api.delete(
      endpoint
    );

    return normalizeApiMessage(
      res.data
    );
  } catch (error) {
    return normalizeApiError(
      error
    );
  }
};

// ============================================================
// UPLOAD FILE
// ============================================================

export const UploadFileWithToken = async (
  endpoint,
  formData
) => {
  const res = await api.post(
    endpoint,
    formData,
    {
      headers: {
        "Content-Type":
          "multipart/form-data",
      },
    }
  );

  return normalizeApiMessage(
    res.data
  );
};

// ============================================================
// POST IMAGE
// ============================================================

export const PostImageDataWithToken = async (
  endpoint,
  formData
) => {
  try {
    const res = await api.post(
      endpoint,
      formData,
      {
        headers: {
          "Content-Type":
            "multipart/form-data",
        },
      }
    );

    return normalizeApiMessage(
      res.data
    );
  } catch (error) {
    return normalizeApiError(
      error
    );
  }
};

// ============================================================
// LOGOUT
// ============================================================

export const LogoutUser = () => {
  logoutAndRedirect();
};

export default api;